import { Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Event bus message
 */
export class EventBusMessage {
  /**
   * Topic
   */
  public topic: string;

  /**
   * Payload
   */
  public payload: any;

  /**
   * Options
   */
  public options: any;
}

/**
 * Event bus callback function type
 */
export type EventBusCallbackFn = (payload: any, options: any) => void;

/**
 * Event bus service: Handles broadcasting / subscribing of application-wide events.
 * Note: A component needs to be in scope in order to receive any events
 */
@Injectable({
  providedIn: 'root'
})
export class EventBusService {
  /**
   * Subject that's used for subscriptions
   */
  private eventBus: Subject<EventBusMessage> = new Subject<EventBusMessage>();

  /**
   * Broadcasts an event
   * @param topic Topic of the event
   * @param payload The event payload. Can be null
   * @param options The event options (further parameters). Can be null
   */
  public broadcastEvent(topic: string, payload: any = null, options: any = null): void {
    this.eventBus.next({ topic, payload, options });
  }

  /**
   * Subscribes to an event
   * @param topic Topic of the event
   * @param callbackFn Callback function
   * @returns An Observable that needs to be unsubscribed if the subscription isn't required anymore
   */
  public subscribeEvent(topic: string, callbackFn: EventBusCallbackFn): Subscription {
    return this.eventBus.pipe(filter((message: EventBusMessage) => {
      return message.topic === topic;
    })).subscribe((message: EventBusMessage) => {
      callbackFn(message.payload, message.options);
    });
  }
}
